/**
 * Customer Portal v2 client widgets:
 *  - AvatarUploader      (profile photo upload + remove)
 *  - ChangePasswordForm  (logged-in flow)
 *  - LoginHistoryTab     (recent 50 login/auth events)
 *  - TicketsTab          (create + list + thread view)
 */
import React, { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import {
  Camera, Trash2, Loader2, Lock, Eye, EyeOff, CheckCircle2, AlertCircle, Save,
  Ticket, Plus, MessageCircle, Send, X, Clock, ChevronRight, Mail, Phone,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { useCustomerAuth, formatApiError } from '../contexts/CustomerAuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/customers`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const fmtDate = (s) => s ? new Date(s).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—';

export function AvatarUploader() {
  const { customer, token, updateProfile } = useCustomerAuth();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const inputRef = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) { setErr('Image must be <3MB'); return; }
    setBusy(true); setErr('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/me/avatar`, fd, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // refresh profile state
      await updateProfile({});
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm('Remove profile photo?')) return;
    setBusy(true); setErr('');
    try {
      await axios.delete(`${API}/me/avatar`, { headers: { Authorization: `Bearer ${token}` } });
      await updateProfile({});
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const avatarSrc = customer?.avatar_url ? `${BACKEND}${customer.avatar_url}` : null;
  const initials = (customer?.name || '?').split(' ').map(s => s[0]).join('').slice(0, 2).toUpperCase();

  return (
    <div className="flex items-center gap-4 mb-5" data-testid="avatar-uploader">
      <div className="relative w-20 h-20 rounded-full overflow-hidden bg-emerald-100 ring-2 ring-emerald-200 flex items-center justify-center">
        {avatarSrc ? (
          <img src={avatarSrc} alt="avatar" className="w-full h-full object-cover" />
        ) : (
          <span className="text-emerald-700 font-bold text-2xl">{initials}</span>
        )}
        {busy && <div className="absolute inset-0 bg-black/40 flex items-center justify-center"><Loader2 size={20} className="text-white animate-spin" /></div>}
      </div>
      <div className="space-y-1.5">
        <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => inputRef.current?.click()} disabled={busy} data-testid="avatar-upload-btn">
            <Camera size={14} className="mr-1.5" /> {customer?.avatar_url ? 'Replace' : 'Upload'} photo
          </Button>
          {customer?.avatar_url && (
            <Button variant="outline" size="sm" onClick={remove} disabled={busy} className="text-red-600" data-testid="avatar-remove-btn">
              <Trash2 size={14} className="mr-1.5" /> Remove
            </Button>
          )}
        </div>
        <p className="text-[11px] text-slate-500">JPG/PNG/WEBP · max 3MB</p>
        {err && <p className="text-xs text-red-600 inline-flex items-center gap-1"><AlertCircle size={12} /> {err}</p>}
      </div>
    </div>
  );
}

export function ChangeContactForm({ kind }) {
  // kind = 'email' | 'mobile'
  const { token, updateProfile, customer } = useCustomerAuth();
  const [step, setStep] = useState(1); // 1: request, 2: verify
  const [newValue, setNewValue] = useState('');
  const [currentPw, setCurrentPw] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [otp, setOtp] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');

  const label = kind === 'email' ? 'Email' : 'Mobile';
  const placeholder = kind === 'email' ? 'newemail@example.com' : '9876543210';
  const current = kind === 'email' ? customer?.email : customer?.mobile;

  const reset = () => { setStep(1); setNewValue(''); setCurrentPw(''); setOtp(''); setErr(''); setMsg(''); };

  const request = async (e) => {
    e.preventDefault();
    if (!newValue.trim() || !currentPw) { setErr('All fields are required'); return; }
    setBusy(true); setErr('');
    try {
      const r = await axios.post(`${API}/me/change-${kind}/request`, { new_value: newValue.trim(), current_password: currentPw }, { headers: { Authorization: `Bearer ${token}` } });
      setMsg(r.data.message);
      setStep(2);
    } catch (e2) { setErr(formatApiError(e2)); }
    finally { setBusy(false); }
  };

  const verify = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) { setErr('Enter the 6-digit OTP'); return; }
    setBusy(true); setErr('');
    try {
      await axios.post(`${API}/me/change-${kind}/verify`, { new_value: newValue.trim(), otp }, { headers: { Authorization: `Bearer ${token}` } });
      await updateProfile({});
      setMsg(`${label} updated successfully`);
      setStep(3);
      setTimeout(reset, 3000);
    } catch (e2) { setErr(formatApiError(e2)); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5 max-w-md" data-testid={`change-${kind}-form`}>
      <h3 className="font-bold text-slate-900 flex items-center gap-2 mb-1">
        {kind === 'email' ? <Mail size={16} /> : <Phone size={16} />} Change {label}
      </h3>
      <p className="text-xs text-slate-500 mb-3">Current: <span className="font-mono">{current || '—'}</span></p>

      {step === 1 && (
        <form onSubmit={request} className="space-y-3">
          <div>
            <Label className="text-xs">New {label}</Label>
            <Input value={newValue} onChange={(e) => setNewValue(e.target.value)} placeholder={placeholder} type={kind === 'email' ? 'email' : 'tel'} maxLength={kind === 'mobile' ? 10 : undefined} data-testid={`change-${kind}-new`} required />
          </div>
          <div>
            <Label className="text-xs">Current password</Label>
            <div className="relative">
              <Input value={currentPw} onChange={(e) => setCurrentPw(e.target.value)} type={showPw ? 'text' : 'password'} className="pr-10" data-testid={`change-${kind}-pw`} required />
              <button type="button" onClick={() => setShowPw(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">{showPw ? <EyeOff size={14} /> : <Eye size={14} />}</button>
            </div>
          </div>
          {err && <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg p-2 inline-flex items-center gap-1.5"><AlertCircle size={14} /> {err}</div>}
          <Button type="submit" disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`change-${kind}-request-btn`}>
            {busy ? <Loader2 size={14} className="animate-spin mr-2" /> : null} Send OTP
          </Button>
          {kind === 'mobile' && <p className="text-[10px] text-slate-400">OTP will be sent to your registered email (SMS gateway not configured).</p>}
        </form>
      )}

      {step === 2 && (
        <form onSubmit={verify} className="space-y-3">
          {msg && <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg p-2 inline-flex items-start gap-1.5"><CheckCircle2 size={14} className="mt-0.5" /> {msg}</div>}
          <div>
            <Label className="text-xs">6-digit OTP</Label>
            <Input value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="••••••" maxLength={6} inputMode="numeric" className="h-11 text-center tracking-[0.5em] font-bold text-lg" data-testid={`change-${kind}-otp`} required />
          </div>
          {err && <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg p-2 inline-flex items-center gap-1.5"><AlertCircle size={14} /> {err}</div>}
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={reset}>Cancel</Button>
            <Button type="submit" disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 flex-1" data-testid={`change-${kind}-verify-btn`}>
              {busy ? <Loader2 size={14} className="animate-spin" /> : 'Verify & update'}
            </Button>
          </div>
          <button type="button" onClick={() => setStep(1)} className="block mx-auto text-xs text-slate-500 hover:underline">Didn&apos;t receive? Send again</button>
        </form>
      )}

      {step === 3 && (
        <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg p-3 inline-flex items-center gap-2">
          <CheckCircle2 size={16} /> {msg}
        </div>
      )}
    </div>
  );
}

export function ChangePasswordForm() {
  const { token } = useCustomerAuth();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [showCur, setShowCur] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 6) { setErr('New password must be at least 6 characters'); return; }
    if (next === current) { setErr('New password must differ from current'); return; }
    setBusy(true); setErr(''); setMsg('');
    try {
      await axios.post(`${API}/me/change-password`, {
        current_password: current, new_password: next,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setMsg('Password updated');
      setCurrent(''); setNext('');
      setTimeout(() => setMsg(''), 3000);
    } catch (e2) { setErr(formatApiError(e2)); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="bg-white border border-slate-200 rounded-2xl p-5 max-w-md space-y-3" data-testid="change-password-form">
      <h3 className="font-bold text-slate-900 flex items-center gap-2"><Lock size={16} /> Change password</h3>
      <div>
        <Label className="text-xs">Current password</Label>
        <div className="relative">
          <Input value={current} onChange={(e) => setCurrent(e.target.value)} type={showCur ? 'text' : 'password'} required className="pr-10" data-testid="cp-current" autoComplete="current-password" />
          <button type="button" onClick={() => setShowCur(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">{showCur ? <EyeOff size={14} /> : <Eye size={14} />}</button>
        </div>
      </div>
      <div>
        <Label className="text-xs">New password</Label>
        <div className="relative">
          <Input value={next} onChange={(e) => setNext(e.target.value)} type={showNew ? 'text' : 'password'} required minLength={6} className="pr-10" data-testid="cp-new" autoComplete="new-password" />
          <button type="button" onClick={() => setShowNew(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">{showNew ? <EyeOff size={14} /> : <Eye size={14} />}</button>
        </div>
      </div>
      {msg && <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg p-2 inline-flex items-center gap-1.5"><CheckCircle2 size={14} /> {msg}</div>}
      {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-2 inline-flex items-center gap-1.5"><AlertCircle size={14} /> {err}</div>}
      <Button type="submit" disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="cp-submit">
        {busy ? <Loader2 size={14} className="animate-spin mr-2" /> : <Save size={14} className="mr-2" />}
        Update password
      </Button>
    </form>
  );
}

export function LoginHistoryTab() {
  const { token } = useCustomerAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    axios.get(`${API}/me/login-history`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setRows(r.data || []))
      .finally(() => setLoading(false));
  }, [token]);
  if (loading) return <Loader2 size={20} className="animate-spin text-slate-400" />;
  if (rows.length === 0) return <div className="text-sm text-slate-500">No login activity yet.</div>;
  const labels = { login: '🔐 Logged in', signup: '🎉 Signed up', password_change: '🔑 Password changed', password_reset: '🔓 Password reset' };
  return (
    <div className="bg-white border border-slate-200 rounded-2xl divide-y divide-slate-100" data-testid="login-history">
      {rows.map(r => (
        <div key={r.id} className="px-4 py-3 flex flex-wrap items-center gap-2 text-sm">
          <span className="font-medium">{labels[r.event_type] || r.event_type}</span>
          {r.ip && <code className="bg-slate-100 px-1.5 py-0.5 rounded text-[10px]">IP {r.ip}</code>}
          {r.ua && <span className="text-[10px] text-slate-400 truncate max-w-[300px]" title={r.ua}>{(r.ua || '').split(') ')[0]}</span>}
          <span className="ml-auto text-xs text-slate-400 flex items-center gap-1"><Clock size={11} /> {fmtDate(r.created_at)}</span>
        </div>
      ))}
    </div>
  );
}

export function TicketsTab() {
  const { token } = useCustomerAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/me/tickets`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setTickets(r.data || []))
      .finally(() => setLoading(false));
  }, [token]);
  useEffect(load, [load]);

  return (
    <div data-testid="tickets-tab">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Support Tickets</h2>
          <p className="text-sm text-slate-500">Raise an issue and we&apos;ll get back to you</p>
        </div>
        <Button onClick={() => setCreating(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="ticket-new-btn">
          <Plus size={14} className="mr-1.5" /> New ticket
        </Button>
      </div>
      {loading && <Loader2 size={20} className="animate-spin text-slate-400" />}
      {!loading && tickets.length === 0 && (
        <div className="border-2 border-dashed border-slate-200 rounded-2xl p-10 text-center bg-white">
          <Ticket size={36} className="mx-auto text-slate-300 mb-3" />
          <div className="font-semibold text-slate-700">No tickets yet</div>
        </div>
      )}
      <div className="space-y-2">
        {tickets.map(t => (
          <button key={t.id} onClick={() => setSelected(t)} className="w-full text-left bg-white border border-slate-200 rounded-xl p-4 hover:border-emerald-300 transition-colors" data-testid={`ticket-${t.ticket_number}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-mono text-xs text-slate-400">{t.ticket_number}</span>
                  <StatusBadge status={t.status} />
                  <PriorityDot p={t.priority} />
                </div>
                <div className="font-semibold text-slate-900">{t.subject}</div>
                <div className="text-xs text-slate-500 mt-1">{(t.messages?.[t.messages.length - 1]?.body || '').slice(0, 120)}…</div>
              </div>
              <ChevronRight size={16} className="text-slate-400" />
            </div>
            <div className="text-[10px] text-slate-400 mt-2">{fmtDate(t.updated_at || t.created_at)} · {t.messages?.length || 0} message{t.messages?.length !== 1 ? 's' : ''}</div>
          </button>
        ))}
      </div>
      <NewTicketModal open={creating} onClose={() => setCreating(false)} onCreated={(t) => { setCreating(false); load(); setSelected(t); }} token={token} />
      <TicketDrawer open={!!selected} onClose={() => { setSelected(null); load(); }} ticket={selected} token={token} />
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    open: 'bg-amber-100 text-amber-700',
    in_review: 'bg-blue-100 text-blue-700',
    assigned: 'bg-indigo-100 text-indigo-700',
    resolved: 'bg-emerald-100 text-emerald-700',
    closed: 'bg-slate-100 text-slate-600',
  };
  return <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold uppercase ${map[status] || 'bg-slate-100 text-slate-600'}`}>{(status || 'open').replace('_', ' ')}</span>;
}

function PriorityDot({ p }) {
  const c = { low: 'bg-slate-400', normal: 'bg-blue-500', high: 'bg-amber-500', urgent: 'bg-red-500' }[p] || 'bg-slate-400';
  return <span className="inline-flex items-center gap-1 text-[10px] text-slate-500"><span className={`w-2 h-2 rounded-full ${c}`}></span>{p || 'normal'}</span>;
}

function NewTicketModal({ open, onClose, onCreated, token }) {
  const [form, setForm] = useState({ subject: '', category: 'service', priority: 'normal', description: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => { if (open) setForm({ subject: '', category: 'service', priority: 'normal', description: '' }); }, [open]);

  if (!open) return null;
  const save = async () => {
    if (form.subject.length < 3) { setErr('Subject too short'); return; }
    if (form.description.length < 10) { setErr('Please describe the issue'); return; }
    setBusy(true); setErr('');
    try {
      const r = await axios.post(`${API}/me/tickets`, form, { headers: { Authorization: `Bearer ${token}` } });
      onCreated(r.data);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="new-ticket-modal">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-bold text-slate-900">Raise a support ticket</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div><Label>Subject</Label><Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="e.g. Service was delayed" data-testid="ticket-subject" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Category</Label>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm" data-testid="ticket-category">
                <option value="service">Service</option><option value="billing">Billing</option><option value="technical">Technical</option><option value="complaint">Complaint</option><option value="other">Other</option>
              </select>
            </div>
            <div>
              <Label>Priority</Label>
              <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm" data-testid="ticket-priority">
                <option value="low">Low</option><option value="normal">Normal</option><option value="high">High</option><option value="urgent">Urgent</option>
              </select>
            </div>
          </div>
          <div><Label>Description</Label><Textarea rows={5} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Describe your issue in detail…" data-testid="ticket-description" /></div>
          {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-2">{err}</div>}
        </div>
        <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="ticket-submit">
            {busy ? <Loader2 size={14} className="animate-spin" /> : 'Raise ticket'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function TicketDrawer({ open, onClose, ticket, token }) {
  const [detail, setDetail] = useState(ticket);
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { setDetail(ticket); }, [ticket]);

  const refresh = useCallback(async () => {
    if (!ticket?.id) return;
    const r = await axios.get(`${API}/me/tickets/${ticket.id}`, { headers: { Authorization: `Bearer ${token}` } });
    setDetail(r.data);
  }, [ticket, token]);

  const reply = async () => {
    if (!body.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API}/me/tickets/${ticket.id}/reply`, { body }, { headers: { Authorization: `Bearer ${token}` } });
      setBody('');
      await refresh();
    } catch (e) { alert(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (!open || !detail) return null;
  const isClosed = detail.status === 'closed';
  return (
    <div className="fixed inset-0 z-50 flex" onClick={onClose}>
      <div className="flex-1 bg-black/40" />
      <div className="w-full max-w-xl bg-white shadow-2xl overflow-y-auto flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="ticket-drawer">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-3 flex items-center justify-between z-10">
          <div>
            <div className="font-mono text-xs text-slate-400">{detail.ticket_number}</div>
            <h3 className="font-bold text-slate-900">{detail.subject}</h3>
            <div className="flex items-center gap-2 mt-1"><StatusBadge status={detail.status} /><PriorityDot p={detail.priority} /></div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        <div className="flex-1 p-4 space-y-3 overflow-y-auto">
          {(detail.messages || []).map(m => (
            <div key={m.id} className={`flex ${m.from === 'customer' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm ${m.from === 'customer' ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-900'}`}>
                <div className="text-[10px] opacity-70 mb-1 font-semibold">{m.from_name} · {fmtDate(m.created_at)}</div>
                <div className="whitespace-pre-line">{m.body}</div>
              </div>
            </div>
          ))}
        </div>
        {!isClosed ? (
          <div className="border-t border-slate-200 p-3 bg-slate-50">
            <div className="flex gap-2">
              <Textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Type a reply…" rows={2} className="flex-1" data-testid="ticket-reply-body" />
              <Button onClick={reply} disabled={busy || !body.trim()} className="bg-emerald-600 hover:bg-emerald-700" data-testid="ticket-reply-submit">
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              </Button>
            </div>
          </div>
        ) : (
          <div className="border-t border-slate-200 p-3 bg-slate-100 text-center text-sm text-slate-500">This ticket is closed. Open a new ticket if you need help.</div>
        )}
      </div>
    </div>
  );
}
